import {
  readExecutionStorageState,
  removeExecutionStorageState,
  subscribeExecutionStorage,
  writeExecutionStorageState,
  type ExecutionStorageState,
} from '@/utils/analysisExecutionStorage';

type ExecutionSyncCallbacks = {
  onExternalRunning?: (draftText: string) => void;
  onExternalCompletedWithResultId?: (analysisResultId: number) => void;
  onExternalCompletedWithText?: (resultText: string) => void;
  onExternalError?: () => void;
  onExternalCleared?: () => void;
};

type PublishRunningOptions = {
  force?: boolean;
};

type Params = {
  organizationId: string;
  documentId: string;
  callbacks: ExecutionSyncCallbacks;
};

export class ExecutionSync {
  private readonly _organizationId: string;
  private readonly _documentId: string;
  private readonly _callbacks: ExecutionSyncCallbacks;

  analysisTypeId: number | null = null;
  isExternalExecutionActive = false;

  private _unsubscribe: (() => void) | null = null;
  private _pendingStorageUpdateTimer: ReturnType<typeof setTimeout> | null = null;
  private _lastStoredDraftText: string | null = null;

  constructor(params: Params) {
    this._organizationId = params.organizationId;
    this._documentId = params.documentId;
    this._callbacks = params.callbacks;
  }

  observe(analysisTypeId: number): void {
    if (this.analysisTypeId === analysisTypeId && this._unsubscribe) {
      return;
    }

    this._clearSubscription();

    this.analysisTypeId = analysisTypeId;
    this._unsubscribe = subscribeExecutionStorage(this._organizationId, this._documentId, analysisTypeId, (state) => {
      this._onExternalState(state);
    });

    const currentState = readExecutionStorageState(this._organizationId, this._documentId, analysisTypeId);

    if (currentState) {
      this._onExternalState(currentState);
    } else {
      this.isExternalExecutionActive = false;
    }
  }

  publishRunning(analysisTypeId: number, draftText: string, options?: PublishRunningOptions): void {
    if (this.analysisTypeId !== analysisTypeId) {
      return;
    }

    this._lastStoredDraftText = draftText;

    const writeState = () => {
      writeExecutionStorageState(this._organizationId, this._documentId, analysisTypeId, {
        status: 'running',
        draftText: this._lastStoredDraftText ?? '',
        updatedAt: Date.now(),
      });
    };

    if (options?.force) {
      this._clearPendingStorageUpdate();
      writeState();

      return;
    }

    if (this._pendingStorageUpdateTimer != null) {
      return;
    }

    this._pendingStorageUpdateTimer = setTimeout(() => {
      this._pendingStorageUpdateTimer = null;
      writeState();
    }, 200);
  }

  publishCompleted(
    analysisTypeId: number,
    params: { analysisResultId: number | null; resultText?: string; analysisTitle?: string },
  ): void {
    if (this.analysisTypeId !== analysisTypeId) {
      return;
    }

    this._clearPendingStorageUpdate();
    this._lastStoredDraftText = null;

    writeExecutionStorageState(this._organizationId, this._documentId, analysisTypeId, {
      status: 'completed',
      analysisResultId: params.analysisResultId,
      resultText: params.resultText,
      analysisTitle: params.analysisTitle,
      updatedAt: Date.now(),
    });
  }

  publishError(analysisTypeId: number): void {
    if (this.analysisTypeId !== analysisTypeId) {
      return;
    }

    this._clearPendingStorageUpdate();
    this._lastStoredDraftText = null;

    writeExecutionStorageState(this._organizationId, this._documentId, analysisTypeId, {
      status: 'error',
      updatedAt: Date.now(),
    });
  }

  scheduleCleanup(analysisTypeId: number): void {
    if (this.analysisTypeId !== analysisTypeId) {
      return;
    }

    removeExecutionStorageState(this._organizationId, this._documentId, analysisTypeId);
  }

  destroy(): void {
    this._clearPendingStorageUpdate();
    this._clearSubscription();
  }

  private _onExternalState(state: ExecutionStorageState | null): void {
    if (!state) {
      this._onExternalCleared();

      return;
    }

    const analysisTypeId = this.analysisTypeId;

    if (analysisTypeId == null) {
      this.isExternalExecutionActive = false;

      return;
    }

    switch (state.status) {
      case 'running':
        this._onExternalRunning(state);
        break;
      case 'completed':
        this._onExternalCompleted(state);
        break;
      case 'error':
        this._onExternalError();
        break;
      default:
        break;
    }
  }

  private _onExternalCleared(): void {
    if (!this.isExternalExecutionActive) {
      return;
    }

    this.isExternalExecutionActive = false;

    this._callbacks.onExternalCleared?.();
  }

  private _onExternalRunning(state: Extract<ExecutionStorageState, { status: 'running' }>): void {
    this.isExternalExecutionActive = true;
    this._callbacks.onExternalRunning?.(state.draftText);
  }

  private _onExternalCompleted(state: Extract<ExecutionStorageState, { status: 'completed' }>): void {
    this.isExternalExecutionActive = false;

    if (state.analysisResultId != null) {
      this._callbacks.onExternalCompletedWithResultId?.(state.analysisResultId);

      return;
    }

    if (state.resultText != null) {
      this._callbacks.onExternalCompletedWithText?.(state.resultText);

      return;
    }

    this._callbacks.onExternalCompletedWithText?.('');
  }

  private _onExternalError(): void {
    this.isExternalExecutionActive = false;
    this._callbacks.onExternalError?.();
  }

  private _clearSubscription(): void {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    this.analysisTypeId = null;
    this.isExternalExecutionActive = false;
  }

  private _clearPendingStorageUpdate(): void {
    if (this._pendingStorageUpdateTimer != null) {
      clearTimeout(this._pendingStorageUpdateTimer);
      this._pendingStorageUpdateTimer = null;
    }
  }
}
